import { useState, useEffect, useCallback, useRef } from 'react'
import {
  getMobileInfo,
  onMobileChange,
  onVirtualKeyboardChange,
  getVisualViewportHeight,
  isVirtualKeyboardOpen,
  MobileInfo,
} from '../utils/mobile'

export const useMobile = () => {
  const [mobileInfo, setMobileInfo] = useState<MobileInfo>(getMobileInfo)

  useEffect(() => {
    const unsubscribe = onMobileChange(setMobileInfo)
    return unsubscribe
  }, [])

  return mobileInfo
}

export const useVirtualKeyboard = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0
  )

  useEffect(() => {
    const unsubscribe = onVirtualKeyboardChange((open, height) => {
      setIsOpen(open)
      setKeyboardHeight(open ? window.innerHeight - height : 0)
      setViewportHeight(height)
    })

    return unsubscribe
  }, [])

  const scrollToCursor = useCallback((editor: any) => {
    if (!isOpen || !editor) return

    const { from, to } = editor.state.selection
    if (from === to) {
      const coords = editor.view.coordsAtPos(from)
      const visualHeight = getVisualViewportHeight()

      if (coords && coords.top > visualHeight - 100) {
        const scrollContainer = editor.view.dom.closest('.editor-container')
        if (scrollContainer) {
          scrollContainer.scrollBy({
            top: coords.top - visualHeight + 150,
            behavior: 'smooth',
          })
        }
      }
    }
  }, [isOpen])

  return {
    isOpen,
    keyboardHeight,
    viewportHeight,
    scrollToCursor,
  }
}

/**
 * 多通道键盘状态检测 Hook
 *
 * 检测通道（任一通道触发即视为键盘打开）：
 * 1. visualViewport.resize + 高度差 > 100px
 * 2. window.resize + 高度差 > 100px
 * 3. focusin/focusout + 目标是可编辑元素
 * 4. native-keyboard-change（原生 IME 边衬区检测，Android 专用）
 *
 * 高度差策略：
 * - 维护 maxViewportHeight，与当前高度差值 > 100px 判定键盘弹出
 * - 屏幕旋转时重置 max
 * - 键盘关闭后 300ms 延迟更新 max（防止动画期间误判）
 */
export const useKeyboardState = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const [toolbarBottom, setToolbarBottom] = useState(0)
  const [lastKeyboardHeight, setLastKeyboardHeight] = useState(
    typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.4) : 300
  )

  useEffect(() => {
    if (typeof window === 'undefined') return

    const vv = window.visualViewport
    let maxViewportH = vv ? vv.height : window.innerHeight
    let focusOpen = false
    let nativeOpen = false
    let nativeHeight = 0
    let closeTimer: number | null = null

    const updateFromViewport = () => {
      const currentH = vv ? vv.height : window.innerHeight
      const innerH = window.innerHeight
      const offsetTop = vv ? vv.offsetTop : 0

      const heightDiff = maxViewportH - currentH
      const viewportOpen = heightDiff > 100
      const bottom = Math.max(0, innerH - currentH - offsetTop)

      const anyOpen = viewportOpen || focusOpen || nativeOpen
      const effectiveHeight = nativeHeight > 0 ? nativeHeight : heightDiff
      // ★ toolbarBottom 仅接受真实键盘证据（原生键盘事件 / visualViewport 实际缩小）：
      // 纯 focusin（编辑器聚焦但键盘未弹出，如浏览器测试/外接键盘）不回退 40% 估值，
      // 否则键盘工具栏会在无键盘时幽灵显示（"始终停在页底"问题的根因）
      const effectiveBottom = nativeHeight > 0 ? nativeHeight : bottom

      if (anyOpen) {
        if (closeTimer !== null) {
          clearTimeout(closeTimer)
          closeTimer = null
        }
        setIsOpen(true)
        setKeyboardHeight(effectiveHeight)
        setToolbarBottom(effectiveBottom)
        setLastKeyboardHeight(effectiveBottom)
      } else {
        if (closeTimer === null) {
          closeTimer = window.setTimeout(() => {
            if (currentH > maxViewportH) {
              maxViewportH = currentH
            }
            setIsOpen(false)
            setKeyboardHeight(0)
            setToolbarBottom(0)
            closeTimer = null
          }, 100)
        }
      }
    }

    // ★ 判定目标是否"需要软键盘"：仅文本输入类元素才视为键盘打开
    // （text/email/tel/url/password/number/search 等可输入文本的 input、
    //   textarea、contenteditable、ProseMirror）。
    // checkbox/radio/button/submit/file/range/color 等点击类控件聚焦时**不**弹键盘，
    // 否则图片管理弹窗勾选、弹窗内按钮等场景会误弹 MobileKeyboardToolbar。
    const isEditableTarget = (target: HTMLElement): boolean => {
      if (target.isContentEditable) return true
      if (target.closest('[contenteditable="true"]')) return true
      if (target.closest('.ProseMirror')) return true
      if (target.tagName === 'TEXTAREA') return true
      if (target.tagName === 'INPUT') {
        const type = (target as HTMLInputElement).type || 'text'
        const textLikeTypes = ['text', 'email', 'tel', 'url', 'password', 'number', 'search', 'date', 'time', 'datetime-local', 'month', 'week']
        return textLikeTypes.includes(type)
      }
      return false
    }

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement
      if (isEditableTarget(target)) {
        focusOpen = true
        updateFromViewport()
      }
    }

    const handleFocusOut = (e: FocusEvent) => {
      const target = e.target as HTMLElement
      if (isEditableTarget(target)) {
        focusOpen = false
        // focusout 是可靠信号，立即关闭，不等待 viewport 恢复
        if (closeTimer !== null) { clearTimeout(closeTimer); closeTimer = null }
        setIsOpen(false)
        setKeyboardHeight(0)
        setToolbarBottom(0)
      }
    }

    const handleNativeKeyboard = (e: Event) => {
      const customEvent = e as CustomEvent<{ visible: boolean; height: number }>
      nativeOpen = customEvent.detail.visible
      // 原生发送的是物理像素，需转换为 CSS 像素
      const dpr = window.devicePixelRatio || 1
      nativeHeight = customEvent.detail.visible
        ? Math.round(customEvent.detail.height / dpr)
        : 0

      if (!customEvent.detail.visible) {
        // 原生确认键盘关闭，立即消失，不等待动画
        if (closeTimer !== null) { clearTimeout(closeTimer); closeTimer = null }
        focusOpen = false
        setIsOpen(false)
        setKeyboardHeight(0)
        setToolbarBottom(0)
      } else {
        updateFromViewport()
      }
    }

    const handleOrientationChange = () => {
      maxViewportH = 0
      setTimeout(() => {
        maxViewportH = vv ? vv.height : window.innerHeight
        updateFromViewport()
      }, 300)
    }

    updateFromViewport()

    if (vv) {
      vv.addEventListener('resize', updateFromViewport)
      vv.addEventListener('scroll', updateFromViewport)
    }
    window.addEventListener('resize', updateFromViewport)
    window.addEventListener('orientationchange', handleOrientationChange)
    window.addEventListener('focusin', handleFocusIn as EventListener)
    window.addEventListener('focusout', handleFocusOut as EventListener)
    window.addEventListener('native-keyboard-change', handleNativeKeyboard as EventListener)

    return () => {
      if (vv) {
        vv.removeEventListener('resize', updateFromViewport)
        vv.removeEventListener('scroll', updateFromViewport)
      }
      window.removeEventListener('resize', updateFromViewport)
      window.removeEventListener('orientationchange', handleOrientationChange)
      window.removeEventListener('focusin', handleFocusIn as EventListener)
      window.removeEventListener('focusout', handleFocusOut as EventListener)
      window.removeEventListener('native-keyboard-change', handleNativeKeyboard as EventListener)
      if (closeTimer !== null) {
        clearTimeout(closeTimer)
      }
    }
  }, [])

  return {
    isOpen,
    keyboardHeight,
    toolbarBottom,
    lastKeyboardHeight,
  }
}

export const useTouchSelection = (editor: any) => {
  const [isSelecting, setIsSelecting] = useState(false)
  const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null)
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null)

  useEffect(() => {
    if (!editor?.view?.dom) return

    const editorDom = editor.view.dom

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0]
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      }
      setSelectionStart({ x: touch.clientX, y: touch.clientY })
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStartRef.current) return

      const touch = e.touches[0]
      const dx = Math.abs(touch.clientX - touchStartRef.current.x)
      const dy = Math.abs(touch.clientY - touchStartRef.current.y)

      if (dx > 10 || dy > 10) {
        setIsSelecting(true)
      }
    }

    const handleTouchEnd = () => {
      if (isSelecting) {
        setTimeout(() => setIsSelecting(false), 100)
      }
      touchStartRef.current = null
    }

    editorDom.addEventListener('touchstart', handleTouchStart, { passive: true })
    editorDom.addEventListener('touchmove', handleTouchMove, { passive: true })
    editorDom.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      editorDom.removeEventListener('touchstart', handleTouchStart)
      editorDom.removeEventListener('touchmove', handleTouchMove)
      editorDom.removeEventListener('touchend', handleTouchEnd)
    }
  }, [editor, isSelecting])

  return {
    isSelecting,
    selectionStart,
  }
}

export const useLongPress = (
  callback: () => void,
  options: {
    delay?: number
    onCancel?: () => void
  } = {}
) => {
  const { delay = 500, onCancel } = options
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isLongPressRef = useRef(false)

  const start = useCallback(() => {
    isLongPressRef.current = false
    timeoutRef.current = setTimeout(() => {
      isLongPressRef.current = true
      callback()
    }, delay)
  }, [callback, delay])

  const clear = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (!isLongPressRef.current && onCancel) {
      onCancel()
    }
  }, [onCancel])

  return {
    onMouseDown: start,
    onMouseUp: clear,
    onMouseLeave: clear,
    onTouchStart: start,
    onTouchEnd: clear,
  }
}

export const useScrollOptimization = (containerRef: React.RefObject<HTMLElement>) => {
  const isScrollingRef = useRef(false)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleScrollStart = () => {
      if (!isScrollingRef.current) {
        isScrollingRef.current = true
        container.classList.add('is-scrolling')
      }

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }

      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false
        container.classList.remove('is-scrolling')
      }, 150)
    }

    container.addEventListener('scroll', handleScrollStart, { passive: true })
    container.addEventListener('touchmove', handleScrollStart, { passive: true })

    return () => {
      container.removeEventListener('scroll', handleScrollStart)
      container.removeEventListener('touchmove', handleScrollStart)
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [containerRef])

  return isScrollingRef
}

export const useEditorKeyboardAvoiding = (editorRef: React.RefObject<any>) => {
  const { isOpen, keyboardHeight } = useVirtualKeyboard()

  useEffect(() => {
    if (!editorRef.current?.view?.dom) return

    const editorDom = editorRef.current.view.dom

    if (isOpen && keyboardHeight > 0) {
      document.body.classList.add('keyboard-open')
      // ★ 底部避让 = 键盘高度 + 键盘工具条高度 + 余量（2026-09-23 三次调整）：
      // 工具条锚定在键盘上沿（三次压缩后总高 33px = 30px 按钮 + 1×2 padding +
      // 1px 边框），原 +20px 时内容底部仍在工具条后面约一行被挡（真机实测
      // 光标行被工具条遮挡）。+41px = 33px 工具条 + 8px 呼吸余量，
      // 保证光标行完整露出工具条上方。
      editorDom.style.paddingBottom = `${keyboardHeight + 41}px`
    } else {
      document.body.classList.remove('keyboard-open')
      editorDom.style.paddingBottom = ''
    }

    return () => {
      document.body.classList.remove('keyboard-open')
      editorDom.style.paddingBottom = ''
    }
  }, [isOpen, keyboardHeight, editorRef])
}

export const useMobileEditor = (editor: any) => {
  const mobileInfo = useMobile()
  const { isOpen: isKeyboardOpen, scrollToCursor } = useVirtualKeyboard()

  useEffect(() => {
    if (!mobileInfo.isMobile || !editor) return

    const handleFocus = () => {
      setTimeout(() => scrollToCursor(editor), 100)
    }

    editor.on('focus', handleFocus)

    return () => {
      editor.off('focus', handleFocus)
    }
  }, [mobileInfo.isMobile, editor, scrollToCursor])

  useEffect(() => {
    if (!mobileInfo.isMobile) return

    const handleResize = () => {
      if (isVirtualKeyboardOpen()) {
        document.body.classList.add('keyboard-open')
      } else {
        document.body.classList.remove('keyboard-open')
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [mobileInfo.isMobile])

  return {
    ...mobileInfo,
    isKeyboardOpen,
  }
}
